<template>
  <div class="modal fade createNewModal" id="AddModalPrepaidAccount" tabindex="-1" ref="addPrepaidAccountModalRef"
    aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header d-block ps-0 pe-0 pt-0 pb-15 pb-md-25">
          <h5 class="modal-title fw-bold text-black">{{ title }}</h5>
        </div>
        <div class="modal-body ps-0 pe-0 pb-0 pt-15 pt-md-25">
          <Form ref="prepaidAccountForm" @submit="addPrepaidAccount" :validation-schema="prepaidAccountSchema">
            <div class="row">

              <!-- Acteur -->
              <div class="col-md-12">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Acteur <span class="text-danger">*</span>
                  </label>
                  <Field name="acteur" v-slot="{ field }">
                    <Multiselect :options="acteurOptions" :searchable="true" track-by="label" label="label"
                      v-model="field.value" v-bind="field" placeholder="Sélectionner l'acteur" :disabled="isUPDATE"
                      :loading="loadingacteurs" :canDeselect="false">
                      <template v-slot:option="{ option }">
                        <span :class="{ 'text-warning': option.hasActiveAccount }">
                          {{ option.label }}
                        </span>
                      </template>
                    </Multiselect>
                  </Field>
                  <ErrorMessage name="acteur" class="text-danger" />

                  <!-- Message d'avertissement si l'acteur sélectionné a déjà un compte -->
                  <div v-if="selectedActeurHasAccount" class="alert alert-warning mt-10 p-10">
                    <i class="flaticon-warning me-2"></i>
                    <strong>Attention :</strong> Cet acteur possède déjà un compte prépayé actif
                    ({{ selectedActeurAccountNumber }}).
                    Vous devez d'abord suspendre ou fermer ce compte avant d'en créer un nouveau.
                  </div>
                </div>
              </div>

              <!-- Devise -->
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Devise <span class="text-danger">*</span>
                  </label>
                  <Field name="currencyCode" v-slot="{ field }">
                    <Multiselect :options="currencyOptions" :searchable="true" track-by="label" label="label"
                      v-model="field.value" v-bind="field" placeholder="Sélectionner la devise" />
                  </Field>
                  <ErrorMessage name="currencyCode" class="text-danger" />
                </div>
              </div>

              <!-- Statut - GRISÉ EN CRÉATION -->
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Statut <span class="text-danger">*</span>
                  </label>
                  <Field name="status" v-slot="{ field }">
                    <Multiselect :options="statusOptions" :searchable="false" track-by="label" label="label"
                      v-model="field.value" v-bind="field" placeholder="Statut" :disabled="!isUPDATE" />
                  </Field>
                  <ErrorMessage name="status" class="text-danger" />
                  <small v-if="!isUPDATE" class="text-muted">
                    Le statut sera automatiquement "Actif" à la création
                  </small>
                </div>
              </div>

              <!-- Solde initial (création uniquement) -->
              <div class="col-md-6" v-if="!isUPDATE">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Solde initial
                  </label>
                  <Field name="initialBalance" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="0.00" />
                  <ErrorMessage name="initialBalance" class="text-danger" />
                  <small class="text-muted">Solde de départ (optionnel, par défaut 0)</small>
                </div>
              </div>

              <!-- Limite de crédit -->
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Limite de crédit (découvert autorisé)
                  </label>
                  <Field name="creditLimit" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="0.00" />
                  <ErrorMessage name="creditLimit" class="text-danger" />
                  <small class="text-muted">Montant maximum de découvert autorisé</small>
                </div>
              </div>

              <!-- Info création -->
              <div class="col-md-12" v-if="!isUPDATE">
                <div class="alert alert-info mt-20">
                  <i class="flaticon-info me-2"></i>
                  <strong>Information :</strong>
                  <ul class="mb-0 mt-10">
                    <li>Le numéro de compte sera généré automatiquement (PP/YYYY/XXXXXX)</li>
                    <li>Un acteur ne peut avoir qu'un seul compte prépayé actif à la fois</li>
                    <li>Les acteurs avec un compte actif sont indiqués par </li>
                  </ul>
                </div>
              </div>

              <!-- Bouton submit -->
              <div class="col-12 text-end mt-20">
                <button
                  class="default-btn transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16"
                  type="submit" :disabled="submitting || (acteurOptions.length === 0 && !isUPDATE)">
                  {{ submitting ? 'Traitement...' : btntext }}
                </button>
              </div>
            </div>
          </Form>
        </div>

        <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal" aria-label="Close"
          @click="resetValue()"></button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted, computed } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import ApiService from '../../services/ApiService';
import * as Yup from 'yup';
import { error, success, hideModal } from '../../utils/utils';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: "AddModalPrepaidAccount",
  components: { Form, Field, ErrorMessage, Multiselect },
  props: {
    item: { type: String, default: '' }
  },
  setup(props, { emit }) {
    const prepaidAccountSchema = Yup.object().shape({
      acteur: Yup.string().required("L'acteur est obligatoire"),
      currencyCode: Yup.string().required("La devise est obligatoire"),
      status: Yup.string().required("Le statut est obligatoire"),
      initialBalance: Yup.number().min(0).nullable(),
      creditLimit: Yup.number().min(0).nullable(),
    });

    const prepaidAccountForm = ref<any>(null);
    const addPrepaidAccountModalRef = ref<HTMLElement | null>(null);
    const isUPDATE = ref(false);
    const title = ref("Créer un compte prépayé");
    const btntext = ref('Créer');
    const submitting = ref(false);
    const loadingacteurs = ref(false);

    const acteurOptions = ref<{
      value: string;
      label: string;
      hasActiveAccount?: boolean;
      activeAccountNumber?: string;
    }[]>([]);

    const selectedActeurHasAccount = computed(() => {
      const values = prepaidAccountForm.value?.values;
      if (!values?.acteur || isUPDATE.value) return false;

      const selectedActeur = acteurOptions.value.find(a => a.value === values.acteur);
      return selectedActeur?.hasActiveAccount || false;
    });

    const selectedActeurAccountNumber = computed(() => {
      const values = prepaidAccountForm.value?.values;
      if (!values?.acteur) return '';

      const selectedActeur = acteurOptions.value.find(a => a.value === values.acteur);
      return selectedActeur?.activeAccountNumber || '';
    });

    const currencyOptions = ref([
      { value: 'XOF', label: 'XOF - Franc CFA' },
      { value: 'EUR', label: 'EUR - Euro' },
      { value: 'USD', label: 'USD - Dollar américain' },
      { value: 'GBP', label: 'GBP - Livre sterling' },
    ]);

    const statusOptions = ref([
      { value: 'ACTIVE', label: 'Actif' },
      { value: 'SUSPENDED', label: 'Suspendu' },
      { value: 'CLOSED', label: 'Fermé' },
    ]);

    const fetchacteurs = async () => {
      loadingacteurs.value = true;
      try {
        console.log(' Chargement des acteurs disponibles...');
        const response = await ApiService.get('/prepaid-account/user-acteurs-creation');

        console.log(' Réponse API:', response.data);

        const acteursData = response.data.data || [];

        console.log(' Acteurs reçus:', acteursData);

        acteurOptions.value = acteursData;

        console.log(' acteurOptions mis à jour:', acteurOptions.value);

        if (acteursData.length === 0) {
          console.warn(' Aucun acteur trouvé pour cet utilisateur');
        }
      } catch (err: any) {
        console.error(' Erreur chargement acteurs:', err);
        error(err.response?.data?.message || 'Erreur lors du chargement des acteurs');
        acteurOptions.value = [];
      } finally {
        loadingacteurs.value = false;
      }
    };

    // UNIQUEMENT EN MODIFICATION
    const getPrepaidAccount = async (id: string) => {
      if (!id) return;

      try {
        console.log('Chargement du compte pour modification:', id);
        const { data } = await ApiService.get(`prepaid-account/${id}`);
        const account = data.data;

        prepaidAccountForm.value?.setValues({
          acteur: account.acteur?.id || '',
          currencyCode: account.currencyCode,
          status: account.status,
          creditLimit: account.creditLimit || null,
        });

        if (account.acteur) {
          acteurOptions.value = [{
            value: account.acteur.id,
            label: account.acteur.nomComplet
          }];
        }

        isUPDATE.value = true;
        title.value = "Modifier le compte prépayé";
        btntext.value = "Modifier";

        emit('openmodal', addPrepaidAccountModalRef.value);
      } catch (err: any) {
        console.error(' Erreur chargement compte:', err);
        error(err.response?.data?.message || 'Erreur chargement compte');
      }
    };

    const addPrepaidAccount = async (values: any) => {
      if (submitting.value) return;

      // Vérification côté client avant soumission
      if (!isUPDATE.value) {
        const selectedActeur = acteurOptions.value.find(a => a.value === values.acteur);
        if (selectedActeur?.hasActiveAccount) {
          error(`Cet acteur possède déjà un compte prépayé actif (${selectedActeur.activeAccountNumber}). Veuillez d'abord suspendre ou fermer ce compte.`);
          return;
        }
      }

      submitting.value = true;

      try {
        if (isUPDATE.value) {
          await ApiService.put(`prepaid-account/${props.item}`, {
            status: values.status,
            creditLimit: values.creditLimit || null
          });
          success(' Compte modifié avec succès');
        } else {
          await ApiService.post('prepaid-account', {
            acteurId: values.acteur,
            currencyCode: values.currencyCode || 'XOF',
            initialBalance: values.initialBalance || 0,
            creditLimit: values.creditLimit || null
          });
          success(' Compte créé avec succès');
        }

        hideModal(addPrepaidAccountModalRef.value);
        emit('close');
        resetForm();
      } catch (err: any) {
        console.error(' Erreur soumission:', err);
        error(err.response?.data?.message || 'Une erreur est survenue');
      } finally {
        submitting.value = false;
      }
    };

    const resetForm = () => {
      prepaidAccountForm.value?.resetForm();
      acteurOptions.value = [];
      isUPDATE.value = false;
      title.value = "Créer un compte prépayé";
      btntext.value = 'Créer';

      setTimeout(() => {
        prepaidAccountForm.value?.setFieldValue('status', 'ACTIVE');
        prepaidAccountForm.value?.setFieldValue('currencyCode', 'XOF');
      }, 50);
    };

    const resetValue = () => {
      resetForm();
    };

    watch(() => props.item, (newId) => {
      console.log('Watch déclenché, item:', newId);

      if (newId && newId !== '') {
        console.log(' Mode MODIFICATION');
        getPrepaidAccount(newId);
      } else {
        // Mode création
        console.log('✨ Mode CRÉATION');
        isUPDATE.value = false;
        title.value = "Créer un compte prépayé";
        btntext.value = 'Créer';
        resetForm();
        fetchacteurs();
      }
    }, { immediate: true });

    onMounted(() => {

      prepaidAccountForm.value?.setFieldValue('status', 'ACTIVE');
      prepaidAccountForm.value?.setFieldValue('currencyCode', 'XOF');
    });

    return {
      prepaidAccountSchema,
      prepaidAccountForm,
      addPrepaidAccountModalRef,
      addPrepaidAccount,
      title,
      btntext,
      isUPDATE,
      acteurOptions,
      currencyOptions,
      statusOptions,
      resetValue,
      submitting,
      loadingacteurs,
      selectedActeurHasAccount,
      selectedActeurAccountNumber
    };
  },
});
</script>